import { UserAvatar } from "components/UserAvatar";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Paths from "routes/Paths";
import { useSession } from "Session";

import ViewIcon from "./ViewIcon";

import styles from "./UserMenu.module.css";

const getInitials = (name: string): string =>
	name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((word: string) => word[0]?.toUpperCase() ?? "")
		.join("") || "U";

/**
 * Header avatar with a dropdown of account actions — the same destinations
 * as the home sidebar: Shared with Me, Settings, and Log out.
 */
const UserMenu = () => {
	const { t } = useTranslation("homePage");
	const navigate = useNavigate();
	const { signOut, user } = useSession();
	const [isOpen, setIsOpen] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);

	// Close when clicking anywhere outside the avatar or its dropdown.
	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}

		const onDocumentClick = (event: MouseEvent): void => {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		document.addEventListener("mousedown", onDocumentClick);

		return () => document.removeEventListener("mousedown", onDocumentClick);
	}, [isOpen]);

	const toggleOpen = useCallback((): void => setIsOpen((open: boolean) => !open), []);

	const onSharedWithMe = useCallback((): void => {
		setIsOpen(false);
		navigate(Paths.sharedWithMe);
	}, [navigate]);

	const onSettings = useCallback((): void => {
		setIsOpen(false);
		navigate(Paths.settings);
	}, [navigate]);

	const onHelpSupport = useCallback((): void => {
		setIsOpen(false);
		navigate(Paths.helpSupport);
	}, [navigate]);

	const viewerInitials = useMemo(() => getInitials(user?.fullName ?? ""), [user?.fullName]);

	return (
		<div ref={wrapperRef} className={styles.wrapper}>
			<button
				aria-expanded={isOpen}
				aria-haspopup="menu"
				aria-label={t("view.accountMenu")}
				className={styles.avatarButton}
				onClick={toggleOpen}
				type="button">
				<UserAvatar
					className={styles.avatar}
					imageUrl={user?.profileImageUrl}
					initials={viewerInitials}
					title={user?.fullName ?? ""}
				/>
			</button>

			{isOpen && (
				<div className={styles.dropdown} role="menu">
					<button className={styles.menuItem} onClick={onSharedWithMe} role="menuitem" type="button">
						<ViewIcon name="people" />
						<span>{t("navigation.sharedWithMe")}</span>
					</button>
					<button className={styles.menuItem} onClick={onSettings} role="menuitem" type="button">
						<ViewIcon name="settings" />
						<span>{t("navigation.settings")}</span>
					</button>
					<button className={styles.menuItem} onClick={onHelpSupport} role="menuitem" type="button">
						<ViewIcon name="help" />
						<span>{t("navigation.helpSupport")}</span>
					</button>
					<span aria-hidden="true" className={styles.divider} />
					<button
						className={`${styles.menuItem} ${styles.logoutItem}`}
						onClick={signOut}
						role="menuitem"
						type="button">
						<ViewIcon name="logout" />
						<span>{t("navigation.logout")}</span>
					</button>
				</div>
			)}
		</div>
	);
};

export default UserMenu;
